import { HttpResponseDto } from "@/common/api/datasources/Api";
import { api } from "@/common/api/instance";
import { useMutation, useQuery } from "@tanstack/react-query";

interface ResetPasswordDto {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

interface GeneratePasswordResetDto {
  email: string;
}

interface ValidatePasswordResetDto {
  email: string;
  code: string;
}

interface SendVerificationCodeDto {
  type: "email" | "phone";
  value: string;
}

interface VerifyCodeDto {
  type: "email" | "phone";
  value: string;
  code: string;
}

interface SendVerificationCodeRequestDto {
  type: "emailVerificationCode" | "phoneVerificationCode";
  identifier: string;
}

interface VerifyCodeRequestDto {
  type: "emailVerificationCode" | "phoneVerificationCode";
  identifier: string;
  code: string;
}

export const userVerificationService = {
  getVerification: async () => {
    const response = await api.usersVerifications.usersVerificationsControllerGetVerification();
    return response.data;
  },

  generatePasswordReset: async (data: GeneratePasswordResetDto) => {
    const response = await api.usersVerifications.usersVerificationsControllerGeneratePasswordReset(data);
    return response.data;
  },

  validatePasswordReset: async (data: ValidatePasswordResetDto) => {
    const response = await api.usersVerifications.usersVerificationsControllerValidatePasswordReset(data);
    return response.data;
  },

  resetPassword: async (data: ResetPasswordDto) => {
    const response = await api.usersVerifications.usersVerificationsControllerResetPassword(data);
    return response.data;
  },
};

export const useUserVerificationQuery = () => {
  const verificationQuery = useQuery({
    queryKey: ['user-verification'],
    queryFn: () => userVerificationService.getVerification(),
  });

  const sendVerificationCodeMutation = useMutation<
    HttpResponseDto,
    Error,
    SendVerificationCodeDto
  >({
    mutationFn: async (data) => {
      const response = await api.usersVerifications.usersVerificationsControllerSendVerificationCode(
        { type: data.type === "email" ? "emailVerificationCode" : "phoneVerificationCode" }
      );
      return response.data;
    },
  });

  const verifyCodeMutation = useMutation<HttpResponseDto, Error, VerifyCodeDto>({
    mutationFn: async (data) => {
      const response = await api.usersVerifications.usersVerificationsControllerVerifyVerificationCode(
        {
          type: data.type === "email" ? "emailVerificationCode" : "phoneVerificationCode",
          code: data.code
        }
      );
      return response.data;
    },
  });

  const generatePasswordResetMutation = useMutation<
    HttpResponseDto,
    Error,
    { email: string }
  >({
    mutationFn: async (data) => {
      return userVerificationService.generatePasswordReset(data);
    },
  });

  const resetPasswordMutation = useMutation<
    HttpResponseDto,
    Error,
    ResetPasswordDto
  >({
    mutationFn: async (data) => {
      return userVerificationService.resetPassword(data);
    },
  });

  return {
    verificationQuery,
    sendVerificationCodeMutation,
    verifyCodeMutation,
    generatePasswordResetMutation,
    resetPasswordMutation,
  };
}; 